import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Form, Nav, Spinner, Table } from "react-bootstrap";
import { Link } from "react-router-dom";
import { FaPlus, FaEye, FaEdit, FaTrash, FaPaperclip, FaPaperPlane, FaSyncAlt } from "react-icons/fa";
import api from "../../../api";
import { Pill } from "../performance/MyKraKpiPerformancePage";
import {
  EVIDENCE_STATUS_META, STATUS_META, defaultMonthFor, fiscalYearLabelFor, formatKpiValue, formatMonthKey,
  monthKeyFor, monthsForFiscalYear,
} from "../performance/performanceFormat";
import { formatPreview, primaryDate, recordEvidenceMeta, rolePrefix, statusLabel } from "./kraWorkFormat";
import WorkRecordModal from "./WorkRecordModal";
import PrepareEvidenceModal from "./PrepareEvidenceModal";
import "../../tasks/PortalHomeCards.css";
import "../performance/MyKraKpiPerformancePage.css";
import "./MyKraWorkPage.css";

// "My KRA Work" -- the Data Analyst's operational records, in five KRA work
// areas. Records are the actual work (sessions, validations, submissions,
// SOP reviews ...); "Prepare evidence" turns a month's records into the KPI's
// monthly entry, which the reviewer approves before it reaches My
// Performance. Scoring stays on My Performance.

function readUrlParams() {
  if (typeof window === "undefined") return {};
  const p = new URLSearchParams(window.location.search);
  return { section: p.get("section"), type: p.get("type"), month: p.get("month"), add: p.get("add") === "1" };
}

function fyOfMonthKey(key) {
  const [y, m] = key.split("-").map(Number);
  const start = m >= 4 ? y : y - 1;
  return `${start}-${String((start + 1) % 100).padStart(2, "0")}`;
}

function KpiChip({ kpi, typeMeta, monthPreview, onPrepare }) {
  const canPrepare = kpi.feeds_kpi_evidence && typeMeta?.calculates_kpi_value
    && !["AWAITING_REVIEW"].includes(kpi.evidence_status) && kpi.evidence_status !== "APPROVED";
  return (
    <div className="kw-kpi">
      <div className="kw-kpi-main">
        <span className="kw-kpi-label">KPI</span>
        <strong>{kpi.kpi_name}</strong>
        <div className="kw-kpi-pills">
          <Pill meta={STATUS_META[kpi.status]} />
          {kpi.evidence_status && kpi.evidence_status !== "NOT_SUBMITTED" && <Pill meta={EVIDENCE_STATUS_META[kpi.evidence_status]} />}
        </div>
      </div>
      <div className="kw-kpi-num">
        <span className="kw-kpi-label">This month (approved)</span>
        <span>{formatKpiValue(kpi.calculation_type, kpi.current_month)}</span>
      </div>
      <div className="kw-kpi-num">
        <span className="kw-kpi-label">FY to date</span>
        <span>{formatKpiValue(kpi.calculation_type, kpi.ytd)}</span>
      </div>
      {kpi.feeds_kpi_evidence && monthPreview && (
        <div className="kw-kpi-num">
          <span className="kw-kpi-label">Records this month</span>
          <span>{formatPreview(monthPreview) ?? "—"}</span>
        </div>
      )}
      <div className="kw-kpi-action">
        {canPrepare ? (
          <button type="button" className="tlp-btn tlp-btn-primary tlp-btn-sm" onClick={() => onPrepare(kpi)}>
            <FaPaperPlane /> Prepare evidence
          </button>
        ) : !kpi.feeds_kpi_evidence ? (
          <span className="kp-sub kw-note">{typeMeta?.kpi_feed_note || "This KPI is entered or rated on My Performance."}</span>
        ) : null}
      </div>
    </div>
  );
}

function RecordTypeCard({ typeSummary, typeMeta, records, scope, onAdd, onView, onEdit, onDelete, onPrepare }) {
  return (
    <div className="tlp-card kw-type" id={`kw-${typeSummary.record_type}`}>
      <div className="tlp-card-head">
        <div>
          <strong>{typeSummary.label}</strong>
          <span className="kp-sub">{typeSummary.month_count} this month · {typeSummary.ytd_count} financial year to date</span>
        </div>
        <button type="button" className="tlp-btn tlp-btn-primary tlp-btn-sm" onClick={() => onAdd(typeMeta)} aria-label={`Add ${typeSummary.label} record`}>
          <FaPlus /> Add Record
        </button>
      </div>
      {typeSummary.kpis.map((kpi) => (
        <KpiChip key={kpi.kpi_id} kpi={kpi} typeMeta={typeMeta} monthPreview={typeSummary.month_preview} onPrepare={onPrepare} />
      ))}
      {typeSummary.fy_context?.length > 0 && (
        <div className="kw-facts" aria-label={`${typeSummary.label} financial year facts`}>
          {typeSummary.fy_context.map((f) => (
            <span key={f.key} className="kw-fact"><span className="kw-kpi-label">{f.label}</span> <strong>{f.value}</strong></span>
          ))}
          {typeSummary.kpis[0]?.annual_target_label && <span className="kp-sub">{typeSummary.kpis[0].annual_target_label}</span>}
        </div>
      )}
      {typeMeta?.qualifying_note && <p className="kp-sub kw-rule">{typeMeta.qualifying_note}</p>}
      {records.length === 0 ? (
        <div className="kw-empty">
          <span>No data recorded for this {scope === "YTD" ? "financial year" : "period"}.</span>
          <button type="button" className="tlp-btn tlp-btn-ghost tlp-btn-sm" onClick={() => onAdd(typeMeta)}><FaPlus /> Add Record</button>
        </div>
      ) : (
        <div className="table-responsive kp-table-wrap">
          <Table className="tlp-table kp-table mb-0" size="sm">
            <thead>
              <tr>
                <th>Record</th>
                <th>Date</th>
                <th>School</th>
                <th>Status</th>
                <th>Evidence</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {records.map((r) => (
                <tr key={r.id}>
                  <td data-label="Record">
                    <button type="button" className="kp-link" onClick={() => onView(typeMeta, r)}>{r.title}</button>
                    {scope === "YTD" && <span className="kp-sub">{formatMonthKey(r.period_month)}</span>}
                  </td>
                  <td data-label="Date">{primaryDate(r)}</td>
                  <td data-label="School">{r.school_name || "—"}</td>
                  <td data-label="Status">{statusLabel(typeMeta, r.status)}</td>
                  <td data-label="Evidence">
                    <Pill meta={recordEvidenceMeta(r)} />
                    {r.attachment_count > 0 && <span className="kp-sub"><FaPaperclip /> {r.attachment_count}</span>}
                  </td>
                  <td data-label="Actions" className="kp-actions">
                    <button type="button" className="tlp-btn tlp-btn-ghost tlp-btn-sm" onClick={() => onView(typeMeta, r)} aria-label={`View ${r.title}`}>
                      <FaEye /> View Details
                    </button>
                    {r.can_edit && (
                      <>
                        <button type="button" className="tlp-btn tlp-btn-ghost tlp-btn-sm" onClick={() => onEdit(typeMeta, r)} aria-label={`Edit ${r.title}`}>
                          <FaEdit /> Edit
                        </button>
                        <button type="button" className="tlp-btn tlp-btn-ghost tlp-btn-sm" onClick={() => onDelete(r)} aria-label={`Delete ${r.title}`}>
                          <FaTrash />
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}
    </div>
  );
}

const MyKraWorkPage = () => {
  const initial = useMemo(readUrlParams, []);
  const initialMonth = initial.month && /^\d{4}-\d{2}$/.test(initial.month) ? initial.month : null;
  const [fy, setFy] = useState(() => (initialMonth ? fyOfMonthKey(initialMonth) : fiscalYearLabelFor()));
  const [monthNum, setMonthNum] = useState(() => (initialMonth ? Number(initialMonth.slice(5)) : defaultMonthFor(fiscalYearLabelFor())));
  const [section, setSection] = useState(initial.section || "ERP_SUPPORT");
  const [scope, setScope] = useState("MONTH");
  const [meta, setMeta] = useState(null);
  const [schools, setSchools] = useState([]);
  const [overview, setOverview] = useState(null);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [modal, setModal] = useState(null); // { typeMeta, record, readOnly }
  const [prepareKpi, setPrepareKpi] = useState(null);
  const [pendingAdd, setPendingAdd] = useState(initial.add ? initial.type : null);

  const monthKey = monthKeyFor(fy, monthNum);
  const monthOptions = useMemo(() => monthsForFiscalYear(fy), [fy]);
  const typeMetaByKey = useMemo(() => new Map((meta?.record_types || []).map((t) => [t.record_type, t])), [meta]);

  useEffect(() => {
    api.get("/analyst-work/meta").then((res) => setMeta(res.data?.data || null))
      .catch((err) => setError(err?.response?.data?.message || "Could not load the KRA work areas."));
    api.get("/schools").then((res) => setSchools(Array.isArray(res.data) ? res.data : [])).catch(() => setSchools([]));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [ov, list] = await Promise.all([
        api.get("/analyst-work/overview", { params: { month: monthKey } }),
        api.get("/analyst-work/records", { params: { month: monthKey, scope, section } }),
      ]);
      setOverview(ov.data?.data || null);
      setRecords(list.data?.data?.records || []);
    } catch (err) {
      setError(err?.response?.data?.message || "Could not load your KRA work records.");
    } finally {
      setLoading(false);
    }
  }, [monthKey, scope, section]);

  useEffect(() => { load(); }, [load]);

  // Deep link from My Performance ("Add Record"): open the form once the
  // schema is available.
  useEffect(() => {
    if (pendingAdd && typeMetaByKey.has(pendingAdd)) {
      setModal({ typeMeta: typeMetaByKey.get(pendingAdd), record: null, readOnly: false });
      setPendingAdd(null);
    }
  }, [pendingAdd, typeMetaByKey]);

  const changeFy = (value) => { setFy(value); setMonthNum(defaultMonthFor(value)); };

  const deleteRecord = async (record) => {
    if (!window.confirm(`Delete "${record.title}"?`)) return;
    try {
      await api.delete(`/analyst-work/records/${record.id}`);
      load();
    } catch (err) {
      setError(err?.response?.data?.message || "Could not delete the record.");
    }
  };

  const sections = overview?.sections || meta?.sections?.map((s) => ({ ...s, record_types: [] })) || [];
  const activeSection = sections.find((s) => s.key === section) || sections[0];
  const fiscalYears = useMemo(() => Array.from(new Set([fiscalYearLabelFor(), fy])).sort().reverse(), [fy]);

  return (
    <div className="tlp-page kp-page kw-page">
      <div className="tlp-header">
        <div>
          <div className="tlp-eyebrow">KRA / KPI</div>
          <h1 className="tlp-title">My KRA Work</h1>
          <p className="tlp-subtitle">
            Record your actual work for {formatMonthKey(monthKey)}. Submitted evidence is reviewed before it counts on{" "}
            <Link to={`${rolePrefix()}/performance`}>My Performance</Link>.
          </p>
        </div>
        <div className="tlp-hactions kp-filters">
          <Form.Group controlId="kw-fy">
            <Form.Label className="kp-filter-label">Financial Year</Form.Label>
            <Form.Select size="sm" value={fy} onChange={(e) => changeFy(e.target.value)}>
              {fiscalYears.map((label) => <option key={label} value={label}>FY {label}</option>)}
            </Form.Select>
          </Form.Group>
          <Form.Group controlId="kw-month">
            <Form.Label className="kp-filter-label">Month</Form.Label>
            <Form.Select size="sm" value={monthNum} onChange={(e) => setMonthNum(Number(e.target.value))}>
              {monthOptions.map((m) => <option key={m.num} value={m.num}>{m.label}</option>)}
            </Form.Select>
          </Form.Group>
          <button type="button" className="tlp-btn tlp-btn-ghost kp-refresh" onClick={load} disabled={loading} aria-label="Refresh">
            <FaSyncAlt className={loading ? "kp-spin" : ""} />
          </button>
        </div>
      </div>

      {error && <div className="alert alert-danger mb-0">{error}</div>}

      <Nav variant="tabs" className="kw-tabs" activeKey={activeSection?.key} onSelect={(k) => setSection(k)}>
        {sections.map((s) => (
          <Nav.Item key={s.key}>
            <Nav.Link eventKey={s.key}>{s.order}. {s.label}</Nav.Link>
          </Nav.Item>
        ))}
      </Nav>

      <div className="kw-scope" role="group" aria-label="Period">
        {[["MONTH", formatMonthKey(monthKey)], ["YTD", `FY ${fy} to date`]].map(([key, label]) => (
          <button key={key} type="button" className={`tlp-btn tlp-btn-sm ${scope === key ? "tlp-btn-primary" : "tlp-btn-ghost"}`}
            aria-pressed={scope === key} onClick={() => setScope(key)}>
            {label}
          </button>
        ))}
      </div>

      {loading && !overview ? (
        <div className="tlp-card kp-center"><Spinner animation="border" size="sm" /> <span>Loading your KRA work…</span></div>
      ) : activeSection ? (
        <div className="kw-section">
          {activeSection.key === "ERP_SUPPORT" && (
            <div className="tlp-card kw-type">
              <div className="tlp-card-head">
                <div>
                  <strong>ERP Support Tickets</strong>
                  <span className="kp-sub">Ticket resolution within SLA is calculated automatically from the ERP tickets assigned to you — no record needed here.</span>
                </div>
                <Link className="tlp-btn tlp-btn-ghost tlp-btn-sm" to={`${rolePrefix()}/my-tasks`}>View ERP Tickets</Link>
              </div>
            </div>
          )}
          {activeSection.record_types.map((t) => (
            <RecordTypeCard
              key={t.record_type}
              typeSummary={t}
              typeMeta={typeMetaByKey.get(t.record_type)}
              records={records.filter((r) => r.record_type === t.record_type)}
              scope={scope}
              onAdd={(typeMeta) => typeMeta && setModal({ typeMeta, record: null, readOnly: false })}
              onView={(typeMeta, record) => setModal({ typeMeta, record, readOnly: true })}
              onEdit={(typeMeta, record) => setModal({ typeMeta, record, readOnly: false })}
              onDelete={deleteRecord}
              onPrepare={setPrepareKpi}
            />
          ))}
        </div>
      ) : null}

      {modal && modal.typeMeta && (
        <WorkRecordModal
          typeMeta={modal.typeMeta}
          record={modal.record}
          readOnly={modal.readOnly}
          month={monthKey}
          schools={schools}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); load(); }}
        />
      )}
      {prepareKpi && (
        <PrepareEvidenceModal
          kpi={prepareKpi}
          month={monthKey}
          onClose={() => setPrepareKpi(null)}
          onSubmitted={() => { setPrepareKpi(null); load(); }}
        />
      )}
    </div>
  );
};

export default MyKraWorkPage;
